import { useEffect, useMemo, useState, type KeyboardEventHandler } from 'react';
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { useSiteContext } from '../../state/SiteContext';
import { useOrganizationFilter, useValidityFilter } from '../../state/FilterContext';
import { useConfigurationFilter } from '../../state/FilterContext';
import { services } from '../../globals';
import { ChatbotHistory } from '../../shared/models/chatbot-history.model';
import { useChatbotHistory, useCreateInternalStateBranch } from '../../state/InternalContext';


interface ChatbotPromptProps {
  children: React.ReactNode,
  sendOnEnter?: boolean | string,
}

/**
 * A component that renders a chatbot prompt.
 * @deprecated Use {@link Chatbot} with inline useChat history instead. This component remains for backward-compatible authored HTML.
 */
export function ChatbotPrompt({ children, sendOnEnter }: ChatbotPromptProps): React.ReactElement {
  const [prompt, setPrompt] = useState<string>("");
  const [working, setWorking] = useState(false);
  const sendOnEnterEnabled = sendOnEnter === true || sendOnEnter === "true";

  const { siteState } = useSiteContext();
  const organization = useOrganizationFilter();
  const configuration = useConfigurationFilter();
  const validityFilter = useValidityFilter();

  const chatbotHistory = useChatbotHistory();
  const createInternalStateBranch = useCreateInternalStateBranch();

  const [myChatbotHistory, setMyChatbotHistory] = useState<ChatbotHistory>(chatbotHistory || { prompts: [] });

  useEffect(() => {
    setMyChatbotHistory(chatbotHistory || { prompts: [] });
  }, [chatbotHistory]);

  const handleClick = async () => {
    setPrompt("");
    setWorking(true);
    const promptInfo = await services.chatbot.getContinuation(
      siteState.site?.name || "",
      myChatbotHistory,
      prompt,
      organization,
      configuration,
      { validity: validityFilter },
    );

    setMyChatbotHistory({ prompts: myChatbotHistory.prompts.concat(promptInfo) });
    setWorking(false);
  };

  const handlePromptKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!sendOnEnterEnabled || event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    const value = (event.target as HTMLTextAreaElement).value;
    if (!value || working) {
      return;
    }

    void handleClick();
  };

  const BranchComponent = useMemo(() => createInternalStateBranch({
    chatbotHistory: myChatbotHistory,
  }), [myChatbotHistory, createInternalStateBranch]);

  return (
    <BranchComponent>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "row", gap: "1em" }}>
        <TextField
          sx={{ width: "100%" }}
          id="chatbot-query"
          label="chatbot-query"
          variant="standard"
          multiline={true}
          minRows={5}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handlePromptKeyDown}
          InputProps={{
            multiline: true,
            inputComponent: 'textarea'
          }}
        />
        <Button
          sx={{ alignSelf: "flex-end" }}
          variant="contained"
          color="primary"
          onClick={handleClick}
        >Send</Button>
      </Box>
      {children}
      {working && <Box sx={{ margin: "auto" }}><CircularProgress color="inherit" /></Box>}
    </BranchComponent>
  );
};